import React, {useMemo, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {Minus, Plus, Trash2, ShoppingCart, Sparkles, Star} from 'lucide-react';
import {useCart, lineCharge, memberSavingsPct} from '../context/CartContext';
import {getCartRecurringCadence, type CartLine} from '../types';
import PromoCodeField from '../components/PromoCodeField';
import MembershipNudge from '../components/MembershipNudge';
import Button from '@/shared/components/Button';

function formatDollars(cents: number): string {
    return (cents / 100).toFixed(2);
}

function LineThumbnail({line}: {line: CartLine}) {
    if (line.image_url) {
        return (
            <div className="w-20 h-20 rounded-lg bg-mld-menu-background flex-shrink-0 overflow-hidden flex items-center justify-center">
                <img src={line.image_url} alt={line.name} className="w-full h-full object-contain p-1" />
            </div>
        );
    }
    if (line.kind === 'custom_kit') {
        return (
            <div className="w-20 h-20 rounded-lg bg-mld-primary/10 flex-shrink-0 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-mld-primary" />
            </div>
        );
    }
    if (line.kind === 'curated_rec') {
        return (
            <div className="w-20 h-20 rounded-lg bg-mld-secondary/15 flex-shrink-0 flex items-center justify-center">
                <Star className="h-6 w-6 text-mld-secondary" />
            </div>
        );
    }
    return (
        <div className="w-20 h-20 rounded-lg bg-mld-menu-background flex-shrink-0 flex items-center justify-center">
            <ShoppingCart className="h-6 w-6 text-mld-muted-text/60" />
        </div>
    );
}

function lineSubLabel(line: CartLine): string {
    if (line.kind === 'product') return line.category ?? '';
    if (line.kind === 'custom_kit') {
        return line.mode === 'member' ? 'Membership · billed monthly' : 'One-time custom kit';
    }
    if (line.kind === 'membership') {
        return `My Labs+ · billed ${line.recurring === 'annual' ? 'annually' : 'monthly'}`;
    }
    return 'BioMatch program';
}

const CartPage: React.FC = () => {
    const {items, removeItem, updateQuantity, subtotalCents, itemCount, hasRecurring, isMember, usesMemberPricing} = useCart();
    const navigate = useNavigate();

    const [promoCode, setPromoCode] = useState<string | null>(null);
    const [discountCents, setDiscountCents] = useState(0);

    const totalCents = Math.max(0, subtotalCents - discountCents);
    const savingsPct = useMemo(() => memberSavingsPct(items), [items]);
    const recurringCadence = useMemo(() => getCartRecurringCadence(items), [items]);
    const hasMembershipInCart = items.some(({line}) => line.kind === 'membership');
    const promoSubtotalCents = items.reduce(
        (sum, {line, quantity}) =>
            line.kind === 'product'
                ? sum + lineCharge(line, usesMemberPricing) * quantity
                : sum,
        0,
    );

    if (items.length === 0) {
        return (
            <div className="text-center py-20">
                <ShoppingCart className="h-16 w-16 text-mld-muted-text/60 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-mld-text-primary mb-2">Your cart is empty</h2>
                <p className="text-mld-muted-text mb-6">Browse our at-home testing kits to get started.</p>
                <Link to="/shop" className="inline-block px-6 py-3 bg-mld-success text-white font-semibold rounded-full hover:bg-mld-success/90 transition-colors">
                    Browse Tests
                </Link>
            </div>
        );
    }

    return (
        <div>
            <h1 className="text-3xl font-bold text-mld-text-primary mb-8">Your Cart</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Cart items */}
                <div className="lg:col-span-2 space-y-4">
                    {items.map(({line, quantity}) => {
                        const charge = lineCharge(line, usesMemberPricing);
                        const isSynthetic = line.kind !== 'product';
                        const showsMemberPrice = usesMemberPricing && line.member_price_cents != null;
                        return (
                            <div
                                key={line.key}
                                className="bg-mld-menu-background rounded-xl border border-mld-panel-border p-4 flex gap-4">
                                <LineThumbnail line={line} />

                                <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold text-mld-text-primary truncate">{line.name}</h3>
                                    <p className="text-sm text-mld-muted-text">{lineSubLabel(line)}</p>
                                    <p className="text-sm font-medium text-mld-text-primary mt-1">
                                        ${formatDollars(charge)}
                                        {line.recurring && (
                                            <span className="text-xs text-mld-muted-text">
                                                {line.recurring === 'monthly' ? ' /mo' : ' /yr'}
                                            </span>
                                        )}
                                        {line.kind === 'product' && ' each'}
                                        {showsMemberPrice && (
                                            <span className="ml-1.5 text-[10px] font-medium text-mld-primary">
                                                Member price
                                            </span>
                                        )}
                                    </p>
                                </div>

                                <div className="flex flex-col items-end justify-between">
                                    <button
                                        type="button"
                                        onClick={() => removeItem(line.key)}
                                        className="text-mld-muted-text hover:text-mld-danger transition-colors"
                                        aria-label="Remove item">
                                        <Trash2 className="h-4 w-4" />
                                    </button>

                                    {isSynthetic ? (
                                        <span className="text-[10px] uppercase tracking-wide text-mld-text-secondary">
                                            {line.kind === 'membership' || (line.kind === 'custom_kit' && line.mode === 'member')
                                                ? 'Recurring'
                                                : 'Single charge'}
                                        </span>
                                    ) : (
                                        <div className="flex items-center border border-mld-panel-border rounded-lg">
                                            <button
                                                type="button"
                                                onClick={() => updateQuantity(line.key, quantity - 1)}
                                                disabled={quantity <= 1}
                                                className="px-2 py-1 text-mld-muted-text hover:text-mld-text-primary disabled:opacity-30">
                                                <Minus className="h-3 w-3" />
                                            </button>
                                            <span className="px-3 py-1 text-sm font-medium text-mld-text-primary min-w-[2rem] text-center">
                                                {quantity}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => updateQuantity(line.key, quantity + 1)}
                                                className="px-2 py-1 text-mld-muted-text hover:text-mld-text-primary">
                                                <Plus className="h-3 w-3" />
                                            </button>
                                        </div>
                                    )}

                                    <p className="text-sm font-semibold text-mld-text-primary">
                                        ${formatDollars(charge * quantity)}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Order summary */}
                <div className="lg:col-span-1">
                    <div className="bg-mld-menu-background rounded-xl border border-mld-panel-border p-6 sticky top-8">
                        <h2 className="text-lg font-semibold text-mld-text-primary mb-4">Order Summary</h2>

                        {!isMember && !hasMembershipInCart && (
                            <div className="mb-4">
                                <MembershipNudge savingsPct={savingsPct} />
                            </div>
                        )}

                        {/* Promo code */}
                        {promoSubtotalCents > 0 && <div className="mb-4">
                            <PromoCodeField
                                subtotalCents={promoSubtotalCents}
                                onApply={(code, discount) => { setPromoCode(code); setDiscountCents(discount); }}
                                onClear={() => { setPromoCode(null); setDiscountCents(0); }}
                                appliedCode={promoCode}
                                appliedDiscount={discountCents}
                            />
                        </div>}

                        <div className="space-y-2 mb-4">
                            <div className="flex justify-between text-sm">
                                <span className="text-mld-muted-text">Subtotal ({itemCount} items)</span>
                                <span className="font-medium text-mld-text-primary">${formatDollars(subtotalCents)}</span>
                            </div>
                            {discountCents > 0 && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-mld-success">Discount</span>
                                    <span className="text-mld-success">-${formatDollars(discountCents)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-sm">
                                <span className="text-mld-muted-text">Shipping</span>
                                <span className="text-mld-muted-text">Free</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-mld-muted-text">Tax</span>
                                <span className="text-mld-muted-text">Calculated at checkout</span>
                            </div>
                        </div>

                        <hr className="my-4 border-mld-panel-border" />

                        <div className="flex justify-between text-lg font-bold text-mld-text-primary mb-2">
                            <span>Total</span>
                            <span>${formatDollars(totalCents)}</span>
                        </div>
                        {hasRecurring && recurringCadence && (
                            <p className="mb-6 text-xs text-mld-muted-text">
                                {recurringCadence === 'mixed'
                                    ? 'Includes recurring subscriptions. You’ll be charged today and again according to each item’s billing schedule.'
                                    : `Includes ${recurringCadence === 'annual' ? 'an annual' : 'a monthly'} subscription. You’ll be charged today and on the same date each ${
                                        recurringCadence === 'annual' ? 'year' : 'month'
                                    } thereafter.`}
                            </p>
                        )}

                        <Button variant="primary" size="lg" fullWidth onClick={() => navigate('/shop/checkout')} className="rounded-full">
                            Checkout
                        </Button>

                        <Link to="/shop" className="block text-center text-sm text-mld-text-primary font-medium mt-4 hover:underline">
                            Continue Shopping
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartPage;
